import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const processPhases = [
  {
    title: "START",
    subtitle: "Validate and prototype your idea.",
    items: [
      { name: "Business plan", icon: FiIcons.FiFileText },
      { name: "Technical solution design", icon: FiIcons.FiSettings },
      { name: "Prototyping", icon: FiIcons.FiLayout },
      { name: "Idea validation", icon: FiIcons.FiCheckCircle },
      { name: "Investor starting package", icon: FiIcons.FiBriefcase }
    ]
  },
  {
    title: "BUILD",
    subtitle: "Turn your prototype into a working product.",
    items: [
      { name: "Technical rollout plan", icon: FiIcons.FiMap },
      { name: "MVP development", icon: FiIcons.FiSmartphone },
      { name: "Product market fit validation", icon: FiIcons.FiTarget },
      { name: "Investor Pre-Money Package", icon: FiIcons.FiDollarSign }
    ]
  },
  {
    title: "GROW",
    subtitle: "Become a market leader.",
    items: [
      { name: "360 growth strategy", icon: FiIcons.FiTrendingUp },
      { name: "Product iterations & updates", icon: FiIcons.FiRefreshCw }
    ]
  }
];

const Philosophy = () => {
  return (
    <section id="philosophy" className="py-32 bg-zinc-950 border-t border-zinc-900 overflow-hidden relative">
      {/* Decorative element */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-cyan-900/10 blur-[120px] rounded-full pointer-events-none translate-x-1/2 -translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Top Section: Original Complexity Distilled */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
          <div>
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-6xl font-bold tracking-tighter mb-8 leading-tight">
                Complexity,<br/> Distilled.
              </h2>
              <div className="space-y-6 text-zinc-400 text-lg font-light">
                <p>
                  At Phoinix.tech, we believe that the most powerful software systems are those that hide their immense complexity behind elegantly simple interfaces.
                </p>
                <p>
                  We are not just developers; we are product strategists who understand that deep technical architecture must serve a clear business purpose. By integrating AI at the foundational level, we create systems that adapt, learn, and scale.
                </p>
              </div>
            </motion.div>
          </div>
          
          <div className="relative">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-square border border-zinc-800 rounded-3xl p-8 relative overflow-hidden bg-black flex items-center justify-center"
            >
              {/* Abstract code/system representation */}
              <div className="absolute inset-0 opacity-20 font-mono text-xs text-zinc-500 p-6 overflow-hidden whitespace-pre select-none">
{`class AIArchitecture {
  constructor(complexity) {
    this.core = new NeuralEngine();
    this.scale = Infinity;
  }

  async optimize(dataStream) {
    const insights = await this.core.process(dataStream);
    return Analytics.distill(insights);
  }
}

// System initialization
const phoinix = new AIArchitecture('enterprise');
phoinix.deploy();`}
              </div>
              <div className="w-32 h-32 rounded-full border border-cyan-500/30 flex items-center justify-center relative z-10 backdrop-blur-sm bg-black/50">
                <div className="w-16 h-16 rounded-full bg-cyan-500/20 animate-pulse" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Section: Start, Build, Grow Pipeline */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="border-t border-zinc-900 pt-20"
        >
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-bold tracking-tighter mb-4">The Execution Pipeline</h3>
            <p className="text-zinc-400 font-light max-w-2xl mx-auto">A systematic, phase-driven approach to turning abstract ideas into market-dominating digital products.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {processPhases.map((phase, idx) => (
              <div key={idx} className="relative">
                {/* Phase Header */}
                <div className="mb-8 relative z-10">
                  <div className="bg-zinc-900 border border-zinc-800 text-white font-bold tracking-widest px-6 py-3 rounded-lg flex items-center justify-center mb-4 text-sm shadow-[0_0_15px_rgba(6,182,212,0.05)]">
                    <span className="w-2 h-2 rounded-full bg-cyan-500 mr-3 animate-pulse"></span>
                    {phase.title}
                  </div>
                  <p className="text-zinc-500 text-sm text-center font-light h-10">{phase.subtitle}</p>
                </div>

                {/* Pipeline Items */}
                <div className="relative pl-6 md:pl-8 ml-4 md:ml-6 border-l border-zinc-800/80 space-y-4">
                  {phase.items.map((item, itemIdx) => (
                    <motion.div 
                      key={itemIdx}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 * itemIdx + (idx * 0.2) }}
                      className="relative group"
                    >
                      {/* Node Connection */}
                      <div className="absolute -left-[29px] md:-left-[37px] top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-zinc-900 border-2 border-zinc-700 group-hover:border-cyan-500 group-hover:bg-cyan-500/20 transition-colors z-10" />
                      <div className="absolute -left-[24px] md:-left-[32px] top-1/2 -translate-y-1/2 w-6 h-px bg-zinc-800 group-hover:bg-cyan-500/50 transition-colors" />
                      
                      {/* Card */}
                      <div className="bg-black border border-zinc-800/60 rounded-lg p-4 flex items-center gap-4 group-hover:border-cyan-500/30 group-hover:bg-zinc-900/50 transition-all">
                        <div className="text-zinc-500 group-hover:text-cyan-400 transition-colors">
                          <SafeIcon icon={item.icon} className="text-lg" />
                        </div>
                        <span className="text-sm text-zinc-300 font-medium">{item.name}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
        
      </div>
    </section>
  );
};

export default Philosophy;